import { supabaseClient } from "@/lib/supabase";
import type { Database } from "@/types/database/database";
import type { StateKeyT } from "@/types/database/diaries";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";

type StateWordsRow = Database["public"]["Tables"]["state_words"]["Row"];

export async function selectStateWords(
	key: StateKeyT,
	userId: string,
): Promise<string | null> {
	// First try to get from state_words
	const { data: stateWordsData, error: stateWordsError } = await supabaseClient
		.from("state_words")
		.select("*")
		.eq("state_key", key)
		.eq("user_id", userId)
		.maybeSingle();

	if (stateWordsError) {
		throw stateWordsError;
	}

	if (stateWordsData?.state_words) {
		return stateWordsData.state_words;
	}

	// Fallback to state_definition
	const { data: definitionData, error: definitionError } = await supabaseClient
		.from("state_definitions")
		.select("email_words")
		.eq("state_key", key)
		.maybeSingle();

	if (definitionError) {
		throw definitionError;
	}

	return definitionData?.email_words ?? null;
}

export const createStateQueryKey = (stateKey, userId) => [
	"state-words",
	stateKey,
	userId,
];

export const selectStateWordsQueryOptions = (
	key: StateKeyT,
	userId: string,
) => {
	return queryOptions({
		queryKey: [...createStateQueryKey(key, userId)],
		queryFn: () => selectStateWords(key, userId),
	});
};

export function useSelectStateWordsQuery(key: StateKeyT, userId: string) {
	return useSuspenseQuery(selectStateWordsQueryOptions(key, userId));
}
